/**
 * RENDER — pure interpolation & motion math.
 *
 * No Three.js, no DOM. These are the determinism-respecting helpers the renderer
 * uses to (a) interpolate the marble between two GameState snapshots with `alpha`,
 * (b) recompute mover platform poses as a pure function of the sim tick, and
 * (c) drive framerate-independent critically-damped camera smoothing.
 *
 * Everything here is allocation-light: the hot-path functions write into caller-
 * provided output tuples/arrays so `render()` allocates nothing per frame.
 */

import type { Vec3, Quat, MoverMotion } from '@/types';

/** Two-π, hoisted so the mover hot path doesn't recompute it. */
const TWO_PI = Math.PI * 2;

/** Mutable [x,y,z]; the render layer's internal scratch type. */
export type MVec3 = [number, number, number];
/** Mutable [x,y,z,w]. */
export type MQuat = [number, number, number, number];

/** Linear interpolation of a scalar. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Clamp a scalar into [min, max]. */
export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Map x from [inMin,inMax] to [outMin,outMax], clamped to the output range. */
export function mapClamped(
  x: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
): number {
  if (inMax === inMin) return outMin;
  const t = clamp((x - inMin) / (inMax - inMin), 0, 1);
  return outMin + (outMax - outMin) * t;
}

/**
 * Framerate-independent exponential smoothing factor.
 *
 * Returns the blend t for `current += (target - current) * t` such that the
 * response is identical regardless of frame `dt`. `smoothing` is the fraction of
 * the gap REMAINING after one second (e.g. 0.001 ⇒ ~critically snappy, 0.5 ⇒ soft).
 * This is the standard `1 - smoothing^dt` damping used for non-floaty camera lag.
 */
export function dampFactor(smoothing: number, dt: number): number {
  // smoothing in (0,1): smaller = snappier. Guard the degenerate cases.
  if (smoothing <= 0) return 1;
  if (smoothing >= 1) return 0;
  return 1 - Math.pow(smoothing, dt);
}

/** Vec3 linear interpolation into `out`. Returns `out`. */
export function lerpVec3(out: MVec3, a: Vec3, b: Vec3, t: number): MVec3 {
  out[0] = a[0] + (b[0] - a[0]) * t;
  out[1] = a[1] + (b[1] - a[1]) * t;
  out[2] = a[2] + (b[2] - a[2]) * t;
  return out;
}

/** Squared length of a Vec3 (cheap; avoids the sqrt when only comparing). */
export function lenSqVec3(v: Vec3): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

/** Euclidean length of a Vec3. */
export function lenVec3(v: Vec3): number {
  return Math.sqrt(lenSqVec3(v));
}

/**
 * Normalized linear interpolation of a quaternion into `out` (nlerp).
 *
 * We use nlerp (not slerp) deliberately: between two adjacent sim ticks the
 * rotation delta is tiny, so nlerp is visually indistinguishable from slerp,
 * branch-free, and far cheaper — exactly what the 60fps mobile budget wants.
 * Handles the antipodal case (dot < 0) so we always take the short arc.
 */
export function nlerpQuat(out: MQuat, a: Quat, b: Quat, t: number): MQuat {
  let bx = b[0];
  let by = b[1];
  let bz = b[2];
  let bw = b[3];
  const dot = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (dot < 0) {
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let x = a[0] + (bx - a[0]) * t;
  let y = a[1] + (by - a[1]) * t;
  let z = a[2] + (bz - a[2]) * t;
  let w = a[3] + (bw - a[3]) * t;
  const lenSq = x * x + y * y + z * z + w * w;
  if (lenSq > 0) {
    const inv = 1 / Math.sqrt(lenSq);
    x *= inv;
    y *= inv;
    z *= inv;
    w *= inv;
  } else {
    // Degenerate (opposite quats, t=0.5): fall back to `a`.
    x = a[0];
    y = a[1];
    z = a[2];
    w = a[3];
  }
  out[0] = x;
  out[1] = y;
  out[2] = z;
  out[3] = w;
  return out;
}

/**
 * Deterministic mover offset at a (possibly fractional) tick.
 *
 * Mirrors the FROZEN contract in `course.ts`:
 *   offset(t) = axis * amplitude * sin(2π * (t + phaseTicks) / periodTicks)
 *
 * `tick` may be fractional (tick + alpha) so the platform interpolates as
 * smoothly as the marble. Writes axis*amplitude*sin(...) into `out`. A non-finite
 * or zero period yields a zero offset (a static platform) rather than NaN.
 */
export function moverOffset(out: MVec3, motion: MoverMotion, tick: number): MVec3 {
  const period = motion.periodTicks;
  if (!(period > 0)) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
    return out;
  }
  const phase = TWO_PI * ((tick + motion.phaseTicks) / period);
  const s = Math.sin(phase) * motion.amplitude;
  out[0] = motion.axis[0] * s;
  out[1] = motion.axis[1] * s;
  out[2] = motion.axis[2] * s;
  return out;
}
